import React, { useCallback, useRef, useState } from 'react';
import ButtonGroup from '@mui/material/ButtonGroup';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { Button, ButtonProps } from './Button';
import { styled } from '../../styles';
import { IconName } from '../Icon/types';
import { Icon } from '../Icon';

export interface ButtonWithSelectMenuMenuItemProp {
  label: string;
  startIcon?: IconName;
  endIcon?: IconName;
  value: string;
  disabled?: boolean;
}

export interface ButtonWithSelectMenuProps extends Omit<ButtonProps, 'onClick' | 'variant' | 'children'> {
  onClick?: (e: React.MouseEvent, selectedMenuItem?: ButtonWithSelectMenuMenuItemProp) => void;
  variant?: Exclude<ButtonProps['variant'], 'text'>;
  menuItems: ButtonWithSelectMenuMenuItemProp[];
}

export const ButtonWithSelectMenu = ({
  menuItems,
  onClick,
  variant,
  color,
  disabled,
  sx,
  style,
  className,
  size,
  ...props
}: ButtonWithSelectMenuProps) => {
  const [selectedMenuItem] = useState<ButtonWithSelectMenuMenuItemProp | undefined>(menuItems?.[0]);
  const [isMenuOpen, setMenuOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  const selectNewMenuItemHandler = useCallback(
    (e: React.MouseEvent, item: ButtonWithSelectMenuMenuItemProp) => {
      setMenuOpen(false);
      onClick?.(e, item);
    },
    [onClick]
  );

  const onClickHandler = useCallback(
    (event: React.MouseEvent) => {
      onClick?.(event, selectedMenuItem);
    },
    [onClick, selectedMenuItem]
  );

  const menuItemsToDisplay = menuItems?.filter((item) => item.value !== selectedMenuItem?.value);

  return (
    <>
      <ButtonGroup
        size={size}
        variant={variant}
        color={color as 'primary'}
        sx={sx}
        style={style}
        className={className}
        disabled={disabled}
        ref={anchorRef}
      >
        <Button
          {...props}
          dataTest={props.dataTest || 'button-with-select'}
          startIcon={selectedMenuItem?.startIcon}
          endIcon={selectedMenuItem?.endIcon}
          onClick={onClickHandler}
        >
          {selectedMenuItem?.label ?? ' '}
        </Button>
        <StyledDropdownButton
          dataTest="button-more-actions"
          startIcon="arrow_drop_down"
          disabled={disabled || props.loading}
          onClick={() => setMenuOpen(true)}
        />
      </ButtonGroup>
      {menuItemsToDisplay?.length && (
        <Menu
          data-test="button-menu"
          open={isMenuOpen}
          onClose={() => setMenuOpen(false)}
          anchorEl={anchorRef.current}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {menuItemsToDisplay.map((item) => (
            <MenuItem key={item.value} disabled={item.disabled} onClick={(e) => selectNewMenuItemHandler(e, item)}>
              {item.startIcon && <Icon name={item.startIcon} style={{ marginRight: 8 }} />}
              {item.label}
              {item.endIcon && <Icon name={item.endIcon} style={{ marginLeft: 8 }} />}
            </MenuItem>
          ))}
        </Menu>
      )}
    </>
  );
};

const StyledDropdownButton = styled(Button)`
  .MuiButton-startIcon {
    margin: 0 0;
  }
  min-width: 35px;
  max-width: 35px;
`;
